// @ts-check
import { callExecuteSPERP } from '@/js/services/APIErp';
import { ApiModule, httpClient } from '../api';

/** @import { AxiosResponse } from 'axios' */
/**
 * @import {
 * ActividadProduccion,
 * AcumuladoExistencias,
 * Almacen,
 * Articulo,
 * Cliente,
 * Componente,
 * DatosParteReparacion,
 * ParteProduccionPrevistoEntrada,
 * ParteProduccionRealEntrada,
 * PuestoTrabajo,
 * Respuesta,
 * RespuestaFinalizarParte,
 * Trabajador,
 * UnidadContenedora,
 * } from '@erpagroapp/types' */

const MODULE_PATH = ApiModule.Repairs;

/**
 * Fetches a worker by their username within a specific enterprise group.
 * @param {string} username - The username of the worker to fetch.
 * @param {number} enterpriseGroupId - The ID of the enterprise group to search for the worker.
 * @returns {Promise<AxiosResponse<Trabajador[]>>}
 */
export function callFetchWorkerByUsername(username, enterpriseGroupId) {
  return callExecuteSPERP('APPReparaciones_ObtenerTrabajadorXUsuario', [
    {
      NombreParametro: 'NombreUsuario',
      Valor: username,
    },
    {
      NombreParametro: 'IdGrupoEmpresarial',
      Valor: enterpriseGroupId,
    },
  ], 'TrabajadorJSON');
}

/**
 * Fetches work parts based on the provided filters.
 * @param {number} workerId - The ID of the worker.
 * @param {string} fromDate - The start date for filtering the work parts. (Format: 'YYYY-MM-DD')
 * @param {string|null} toDate - The end date for filtering the work parts. (Format: 'YYYY-MM-DD') (Optional)
 * @param {boolean} finished - Indicates whether to fetch the finished or pending work parts.
 * @param {number|null} [latitude] - The latitude for filtering the work parts. (Optional)
 * @param {number|null} [longitude] - The longitude for filtering the work parts. (Optional)
 * @returns {Promise<AxiosResponse<Record<string, any>>>}
 */
export function callFetchWorkParts(workerId, fromDate, toDate, finished, latitude = null, longitude = null) {
  const params = {
    workerId,
    fromDate,
    toDate,
    finished,
    latitude,
    longitude,
  };
  return httpClient().post(`${MODULE_PATH}/GetWorkParts`, null, { params });
}

/**
 * Fetches work positions within a specific enterprise group.
 * @param {number} enterpriseGroupId - The ID of the enterprise group to fetch work positions for.
 * @returns {Promise<AxiosResponse<PuestoTrabajo[]>>}
 */
export function callFetchWorkPositions(enterpriseGroupId) {
  return callExecuteSPERP('APPReparaciones_ObtenerPuestosTrabajo', [
    {
      NombreParametro: 'IdGrupoEmpresarial',
      Valor: enterpriseGroupId,
    },
  ], 'Comun.PuestoTrabajoJSON');
}

/**
 * Registers a tick action for a worker at a specific work position.
 * @param {number} workerId - The ID of the worker.
 * @param {number} workPositionId - The ID of the work position.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callRegisterTickAction(workerId, workPositionId) {
  const params = {
    workerId,
    workPositionId,
  };
  return httpClient().post(`${MODULE_PATH}/RegisterTickAction`, null, { params });
}

/**
 * Checks whether a worker can finish their day.
 * @param {number} workerId - The ID of the worker.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callCheckWorkerDayCompletion(workerId) {
  const params = {
    workerId,
  };
  return httpClient().post(`${MODULE_PATH}/CanWorkerFinishDay`, null, { params });
}

/**
 * Finishes the latest tick action for a worker.
 * @param {number} workerId - The ID of the worker.
 * @returns {Promise<AxiosResponse<Respuesta>>} A promise that resolves to the HTTP response.
 */
export function callFinishLatestTickAction(workerId) {
  const params = {
    workerId,
  };
  return httpClient().post(`${MODULE_PATH}/FinishLatestTickAction`, null, { params });
}

/**
 * Fetches clients within a specific enterprise group.
 * @param {object} options - An object containing parameters for the request.
 * @param {string} [options.filter] - The filtering value. (Optional)
 * @param {number} options.enterpriseGroupId - The ID of the enterprise group to fetch clients for.
 * @returns {Promise<AxiosResponse<Cliente[]>>}
 */
export function callFetchClients(options) {
  const {
    filter = '',
    enterpriseGroupId,
  } = options;

  return callExecuteSPERP('APPReparaciones_ObtenerClientesXGrupoEmpresarial', [
    {
      NombreParametro: 'Filtro',
      Valor: filter,
    },
    {
      NombreParametro: 'IdGrupoEmpresarial',
      Valor: enterpriseGroupId,
    },
  ], 'Comun.ClienteJSON');
}

/**
 * Fetches production activities within a specific enterprise group for Repairs submodule.
 * @param {object} options - An object containing parameters for the request.
 * @param {string} [options.filter] - The filtering value. (Optional)
 * @param {number} options.enterpriseGroupId - The ID of the enterprise group to fetch production activities for.
 * @returns {Promise<AxiosResponse<ActividadProduccion[]>>}
 */
export function callFetchProductionActivities(options) {
  const {
    filter = '',
    enterpriseGroupId,
  } = options;

  return callExecuteSPERP('APPReparaciones_ObtenerActividadesProduccion', [
    {
      NombreParametro: 'Filtro',
      Valor: filter,
    },
    {
      NombreParametro: 'IdGrupoEmpresarial',
      Valor: enterpriseGroupId,
    },
  ], 'ActividadProduccionJSON');
}

/**
 * Fetches articles with serial number request.
 * @param {object} options - An object containing parameters for the request.
 * @param {number} options.userId - The ID of the user.
 * @param {number} options.enterpriseGroupId - The ID of the enterprise group.
 * @param {Componente} options.component - The component.
 * @param {string | null} [options.filter] - The filtering value. (Optional)
 * @returns {Promise<AxiosResponse<Articulo[]>>}
 */
export function callFetchArticlesWithSerialNumberRequestByComponent({
  userId,
  enterpriseGroupId,
  component,
  filter = null,
}) {
  return callExecuteSPERP('APPERPAGRO_ArticulosConPeticionNumeroSerieXComponente', [
    {
      NombreParametro: 'idUsuario',
      Valor: userId,
    },
    {
      NombreParametro: 'idGrupoEmpresarial',
      Valor: enterpriseGroupId,
    },
    {
      NombreParametro: 'componente',
      Valor: component,
    },
    {
      NombreParametro: 'filtro',
      Valor: filter,
    },
  ], 'Comun.ArticuloJSON');
}

/**
 * Fetches article serial number for a given article ID and serial number.
 * @param {object} options - An object containing parameters for the request.
 * @param {number | null} [options.articleId] - The ID of the article.
 * @param {string} options.serialNumber - The serial number.
 * @returns {Promise<AxiosResponse<UnidadContenedora[]>>}
 */
export function callFetchArticleSerialNumberByArticleAndSerialNumber({
  articleId = null,
  serialNumber,
}) {
  return callExecuteSPERP('APPERPAGRO_ArticuloNumeroSerieXArticuloXNS', [
    {
      NombreParametro: 'idArticulo',
      Valor: articleId,
    },
    {
      NombreParametro: 'numeroSerie',
      Valor: serialNumber,
    },
  ], 'Almacen.UnidadContenedoraJSON');
}

/**
 * Fetches serial numbers for a given client ID, article ID, date and serial number.
 * @param {object} options - An object containing parameters for the request.
 * @param {number} options.clientId - The ID of the client.
 * @param {number | null} [options.articleId] - The ID of the article.
 * @param {string | null} [options.date] - The date.
 * @param {string | null} [options.serialNumber] - The serial number.
 * @returns {Promise<AxiosResponse<UnidadContenedora[]>>}
 */
export function callFetchSerialNumbersByClient({
  clientId,
  articleId = null,
  date = null,
  serialNumber = null,
}) {
  return callExecuteSPERP('APPERPAGRO_ObtenerNumerosSerieCliente', [
    {
      NombreParametro: 'idCliente',
      Valor: clientId,
    },
    {
      NombreParametro: 'idArticulo',
      Valor: articleId,
    },
    {
      NombreParametro: 'fechaConsulta',
      Valor: date,
    },
    {
      NombreParametro: 'numeroSerie',
      Valor: serialNumber,
    },
  ], 'Almacen.UnidadContenedoraJSON');
}

/**
 * Creates an order and part for a client.
 * @param {PartialDeep<DatosParteReparacion>} data - The data for creating the order and part.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callCreateOrderAndPartForClient(data) {
  return httpClient().post(`${MODULE_PATH}/CreateOrderAndPartForClient`, data);
}

/**
 * Fetches the expected workers for a specific work part.
 * @param {number} workPartId - The ID of the work part to fetch expected workers for.
 * @returns {Promise<AxiosResponse<Trabajador[]>>}
 */
export function callFetchExpectedWorkers(workPartId) {
  return callExecuteSPERP('APPReparaciones_ObtenerTrabajadoresPrevistosXParteProduccion', [
    {
      NombreParametro: 'IdParteProduccion',
      Valor: workPartId,
    },
  ], 'TrabajadorJSON');
}

/**
 * Fetches active workers based on the provided enterprise group ID.
 * @param {number} enterpriseGroupId - The ID of the enterprise group.
 * @returns {Promise<AxiosResponse<Trabajador[]>>}
 */
export function callFetchActiveWorkers(enterpriseGroupId) {
  return callExecuteSPERP('APPReparaciones_ObtenerTrabajadoresActivosXParteProduccion', [
    {
      NombreParametro: 'IdParteProduccion',
      Valor: enterpriseGroupId,
    },
  ], 'TrabajadorJSON');
}

/**
 * Fetches inactive workers based on the provided enterprise group ID.
 * @param {number} enterpriseGroupId - The ID of the enterprise group.
 * @returns {Promise<AxiosResponse<Trabajador[]>>}
 */
export function callFetchInactiveWorkers(enterpriseGroupId) {
  return callExecuteSPERP('APPReparaciones_ObtenerTrabajadoresInactivosXParteProduccion', [
    {
      NombreParametro: 'IdParteProduccion',
      Valor: enterpriseGroupId,
    },
  ], 'TrabajadorJSON');
}

/**
 * Starts a route for specified workers and work part.
 * @param {PartialDeep<DatosParteReparacion>} data - The data for starting the route.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callStartRoute(data) {
  return httpClient().post(`${MODULE_PATH}/StartRoute`, data);
}

/**
 * Registers a pause action in the route for specified workers and work part.
 * @param {PartialDeep<DatosParteReparacion>} data - The data for the pause action.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callRegisterPauseAction(data) {
  return httpClient().post(`${MODULE_PATH}/RegisterPauseAction`, data);
}

/**
 * Registers a resume action in the route for a specific worker and work part.
 * @param {PartialDeep<DatosParteReparacion>} data - The data for resuming the route.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callRegisterResumeAction(data) {
  return httpClient().post(`${MODULE_PATH}/RegisterResumeAction`, data);
}

/**
 * Registers a change in the route for a specific worker and work part.
 * @param {PartialDeep<DatosParteReparacion>} data - The data for registering a route change.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callRegisterRouteChange(data) {
  return httpClient().post(`${MODULE_PATH}/RegisterRouteChange`, data);
}

/**
 * Starts a part for specified workers and work part.
 * @param {PartialDeep<DatosParteReparacion>} data - The data for starting the route.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callStartPart(data) {
  return httpClient().post(`${MODULE_PATH}/StartPart`, data);
}

/**
 * Fetches warehouses.
 * @param {number} workPartId - The ID of the work part.
 * @returns {Promise<AxiosResponse<Almacen[]>>}
 */
export function callFetchWarehouses(workPartId) {
  return callExecuteSPERP('APPReparaciones_ObtenerAlmacenesParte', [
    {
      NombreParametro: 'IdParteProduccion',
      Valor: workPartId,
    },
  ], 'AlmacenJSON');
}

/**
 * Fetches accumulated stock from the warehouse.
 * @param {object} options - An object containing optional parameters.
 * @param {string} [options.filter] - The filtering value. (Optional)
 * @param {number} [options.warehouseId] - The ID of the warehouse. (Optional)
 * @param {number} [options.enterpriseGroupId] - The ID of the enterprise group. (Optional)
 * @returns {Promise<AxiosResponse<AcumuladoExistencias[]>>}
 */
export function callFetchAccumulatedStock(options) {
  const {
    filter = '',
    warehouseId = 0,
    enterpriseGroupId = 0,
  } = options;

  return callExecuteSPERP('APPReparaciones_ObtenerAcumuladoExistencias', [
    {
      NombreParametro: 'Filtro',
      Valor: filter,
    },
    {
      NombreParametro: 'IdAlmacen',
      Valor: warehouseId,
    },
    {
      NombreParametro: 'IdGrupoEmpresarial',
      Valor: enterpriseGroupId,
    },
  ], 'Almacen.AcumuladoExistenciasJSON');
}

/**
 * Saves one or multiple articles for a specific work part.
 * @param {PartialDeep<DatosParteReparacion>} data - The data for saving the article.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callSaveArticles(data) {
  return httpClient().post(`${MODULE_PATH}/SaveArticles`, data);
}

/**
 * Deletes a `ParteProduccionRealEntrada` for a specific work part.
 * @param {ParteProduccionRealEntrada} realPart - The `ParteProduccionRealEntrada` object containing the id.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callDeleteArticle(realPart) {
  return httpClient().delete(`${MODULE_PATH}/DeleteArticle`, { data: realPart });
}

/**
 * Modifies the quantity consumed article.
 * @param {ParteProduccionRealEntrada} article - The `ParteProduccionRealEntrada` object containing the id.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callModifyQuantityConsumedArticle(article) {
  return httpClient().post(`${MODULE_PATH}/ModifyQuantityConsumedArticle`, article);
}

/**
 * Fetches the detail of a specific work part.
 * @param {number} workerId - The ID of the worker.
 * @param {number} workPartId - The ID of the work part.
 * @returns {Promise<AxiosResponse<Record<string, any>>>}
 */
export function callFetchWorkPartDetail(workerId, workPartId) {
  const params = {
    workerId,
    workPartId,
  };
  return httpClient().post(`${MODULE_PATH}/GetWorkPartDetail`, null, { params });
}

/**
 * Fetches the real part consumptions for a work part.
 * @param {number} workPartId - The ID of the work part.
 * @returns {Promise<AxiosResponse<ParteProduccionRealEntrada[]>>}
 */
export function callFetchRealConsumptions(workPartId) {
  return callExecuteSPERP('APPReparaciones_ObtenerConsumosRealesXParteProduccion', [
    {
      NombreParametro: 'IdParteProduccion',
      Valor: workPartId,
    },
  ], 'ParteProduccionRealEntradaJSON');
}

/**
 * Fetches the expected part consumptions for a work part.
 * @param {number} workPartId - The ID of the work part.
 * @returns {Promise<AxiosResponse<ParteProduccionPrevistoEntrada[]>>}
 */
export function callFetchExpectedConsumptions(workPartId) {
  return callExecuteSPERP('APPReparaciones_ObtenerConsumosPrevistosXParteProduccion', [
    {
      NombreParametro: 'IdParteProduccion',
      Valor: workPartId,
    },
  ], 'ParteProduccionPrevistoEntradaJSON');
}

/**
 * Fetches the list of machines.
 * @returns {Promise<AxiosResponse>}
 */
export function callFetchMachinesList() {
  return httpClient().post(`${MODULE_PATH}/GetMachinesList`);
}

/**
 * Modifies the part machine information.
 * @param {number} workPartId - The ID of the work part.
 * @param {string} warehouseId - The ID of the warehouse.
 * @param {number} articleId - The ID of the article.
 * @param {string} serialNumber - The serial number of the machine.
 * @returns {Promise<AxiosResponse<Record<string, any>>>}
 */
export function callModifyPartMachine(workPartId, warehouseId, articleId, serialNumber) {
  const params = {
    workPartId,
    warehouseId,
    articleId,
    serialNumber,
  };
  return httpClient().post(`${MODULE_PATH}/ModifyPartMachine`, null, { params });
}

/**
 * Fetches the part history list for a given article and serial number.
 * @param {number} articleId - The ID of the article.
 * @param {string} serialNumber - The serial number of the part.
 * @returns {Promise<AxiosResponse<Record<string, any>>>}
 */
export function callFetchMachineHistory(articleId, serialNumber) {
  const params = {
    articleId,
    serialNumber,
  };
  return httpClient().post(`${MODULE_PATH}/GetPartHistoryList`, null, { params });
}

/**
 * Modifies the observations for a specific work part.
 * @param {number} workPartId - The ID of the work part.
 * @param {string} observations - The observations to be saved.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callModifyPartObservations(workPartId, observations) {
  const params = {
    workPartId,
    observations,
  };
  return httpClient().post(`${MODULE_PATH}/ModifyPartObservations`, null, { params });
}

/**
 * Modifies the observations for a specific order of a work part.
 * @param {number} workPartId - The ID of the work part.
 * @param {string} observations - The observations to be saved.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callModifyOrderObservations(workPartId, observations) {
  const params = {
    workPartId,
    observations,
  };
  return httpClient().post(`${MODULE_PATH}/ModifyOrderObservations`, null, { params });
}

/**
 * Generates articles of type "Horas de Trabajo" (Work Hours) and optionally "Desplazamiento" (Travel)
 * based on the specified parameters.
 * @param {number} workPartId - The ID of the work part.
 * @param {boolean} travel - Indicates whether a travel article should be generated.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callGenerateRealHoursConsumptions(workPartId, travel) {
  const params = {
    workPartId,
    travel,
  };
  return httpClient().post(`${MODULE_PATH}/GenerateRealHoursConsumptions`, null, { params });
}

/**
 * Retrieves the PDF report for a specific work part.
 * @param {number} workPartId - The ID of the work part.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callGetRepairsReport(workPartId) {
  const params = {
    workPartId,
  };
  return httpClient().post(`${MODULE_PATH}/GetRepairsReport`, null, { params });
}

/**
 * Finishes a work part.
 * @param {number} workPartId - The ID of the work part.
 * @param {boolean} valued - Whether the work part is valued or not.
 * @returns {Promise<AxiosResponse<RespuestaFinalizarParte>>}
 */
export function callFinishPart(workPartId, valued) {
  const params = {
    workPartId,
    valued,
  };
  return httpClient().post(`${MODULE_PATH}/FinishPart`, null, { params });
}

/**
 * Retrieves the delivery note of a specific work part along with optional email retrieval.
 * @param {number} workPartId - The ID of the work part.
 * @param {boolean} fetchEmails - Determines whether to fetch email addresses associated with the delivery note. Default is `true`.
 * @returns {Promise<AxiosResponse<RespuestaFinalizarParte>>}
 */
export function callGetRepairsDeliveryNoteReport(workPartId, fetchEmails) {
  const params = {
    workPartId,
    fetchEmails,
  };
  return httpClient().post(`${MODULE_PATH}/GetRepairsDeliveryNoteReport`, null, { params });
}

/**
 * Signs a specific work part with a signature.
 * @param {number} workPartId - The ID of the work part.
 * @param {string} signature - The signature.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callSignPart(workPartId, signature) {
  const params = {
    workPartId,
  };
  return httpClient().post(`${MODULE_PATH}/SignPart`, signature, { params });
}

/**
 * Signs a specific delivery note with a signature.
 * @param {number} deliveryNoteId - The ID of the delivery note.
 * @param {string} signature - The signature.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callSignDeliveryNote(deliveryNoteId, signature) {
  const params = {
    deliveryNoteId,
  };
  return httpClient().post(`${MODULE_PATH}/SignDeliveryNote`, signature, { params });
}

/**
 * Sends a delivery note email for a specific work part.
 * @param {number} workPartId - The ID of the work part.
 * @param {number} deliveryNoteId - The ID of the delivery note.
 * @param {string[]} emails - The list of recipient emails.
 * @param {boolean} emailsModified - Whether the recipient emails were modified or not.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callSendDeliveryNoteEmail(workPartId, deliveryNoteId, emails, emailsModified) {
  const params = {
    workPartId,
    deliveryNoteId,
    emailsModified,
  };
  return httpClient().post(`${MODULE_PATH}/SendDeliveryNoteEmail`, emails, { params });
}

/**
 * Sets the start date for a work part associated with a repair.
 * @param {number} workPartId - The ID of the work part.
 * @param {Date} date - The new start date.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callSetPartStartDate(workPartId, date) {
  const params = {
    workPartId,
    date,
  };
  return httpClient().post(`${MODULE_PATH}/SetPartStartDate`, null, { params });
}
